import { Injectable } from '@angular/core';
import {
  CompareRefiners,
  FilterTypes,
  getMetricFilterAPIParams,
  splitCompositeSubFilter,
} from '@app/core/utilities/refiners.utilities';
import { RulesTab } from '@app/criterion-addition/shared/models/rule.models';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { PerformanceGroupGoalType } from '../../pg-goals/pg-goals.models';
import { AdPlatform, CampaignOptimizationValue, CustomerSummary, SubFilter } from '../models/common.models';
import {
  CLICKS,
  CONVERSIONS,
  COST,
  COST_PER_CONVERSION,
  COST_REVENUE_RATIO,
  IMPRESSIONS,
  RETURN_ON_INVESTMENT,
  ROAS,
} from '../models/metrics.constants';
import {
  accountFilters,
  adGroupFilters,
  CampaignCreationPGFilters,
  campaignFilters,
  campaignsPGFilters,
  criteriaFilters,
  destinationAdGroupFilters,
  goalsPGFilters,
  PGFilters,
  rulesFilters,
  suggestionsFilters,
} from '../models/sub-filters.model';
import { CustomerState } from '../store/states/customer/customer.state';
import { snakeToCamelCase } from '../utilities/string.utilities';

@Injectable({ providedIn: 'root' })
export class SubFiltersService {
  translations;
  summary: CustomerSummary;
  getSelectionForDisplay(selectionQuery, filters) {
    if (!selectionQuery) return '';

    const filterTmp = <SubFilter[]>[...Object.values(filters)];

    const displayQuery = [];
    const selections = selectionQuery.split(';').map((param) => {
      const [key, value] = splitCompositeSubFilter(param);
      return { key, selection: value.split(',') };
    });
    selections.forEach((item) => {
      const tmp = filterTmp.filter((d) => d.key === item.key)?.[0];
      // displayQuery
      switch (tmp?.type) {
        case FilterTypes.multi:
        case FilterTypes.single:
          if (tmp?.type === FilterTypes.multi && item.selection[0].toUpperCase() === 'ALL') {
            // if q param value is ALL select all options
            displayQuery.push(`${tmp.displayName}: ${this.translations.all}`);
          } else {
            if (tmp.key === 'portfolio') displayQuery.push(`${item.selection.length} ${tmp.displayName}`);
            else displayQuery.push(`${tmp.displayName}: ${item.selection.join(',')}`);
          }
          break;
        case FilterTypes.query:
          displayQuery.push(`${tmp.displayName} ${tmp.queryKeyword}: '${item.selection[0]}'`);
          break;
        case FilterTypes.queryMultiple:
          displayQuery.push(`${tmp.displayName} ${tmp.queryKeyword}: '${item.selection[0]}'`);
          break;
        case FilterTypes.switch:
          displayQuery.push(`${tmp.displayName}: ${item.selection[0] === 'true' ? tmp.on : tmp.off}`);
          break;
      }
    });
    return displayQuery.join(', ').trim();
  }
  getSelectedFilters(selectionQuery, filters) {
    if (!selectionQuery) return [];

    const data = [];
    const tmpDefault = selectionQuery.split(';').map((param) => {
      const [key, value] = splitCompositeSubFilter(param);
      return { key, selection: value.split(',') };
    });
    tmpDefault.forEach((item) => {
      filters.forEach((d) => (d.isAdded = d.key === item.key ? true : d.isAdded || false));
      const tmp = filters.filter((d) => d.key === item.key)?.[0];

      switch (tmp?.type) {
        case FilterTypes.single:
        case FilterTypes.multi:
          if (tmp?.type === FilterTypes.multi && item.selection[0].toUpperCase() === 'ALL')
            // if q param value is ALL select all options
            tmp.options.forEach((d) => (d.selected = true));
          else tmp.options.forEach((d) => (d.selected = item.selection.indexOf(d.key.toString()) !== -1));
          break;
        case FilterTypes.query:
          tmp.value = item.selection[0];
          break;
        case FilterTypes.queryMultiple:
          tmp.value = item.selection[0];
          break;
        case FilterTypes.single:
          tmp.value = !!item.selection[0];
          break;
        case FilterTypes.switch:
          tmp.value = item.selection[0] === 'true';
          break;
        case FilterTypes.compare:
          tmp.comparator = item.selection[0];
          tmp.value = item.selection[1];
          break;
      }
      data.push(tmp);
    });
    return data;
  }
  getQueryAndSelectedFilters(data) {
    const filters = [] as { key: string; type: string; value: string; active?: boolean }[];
    const params = [] as { key: string; value: string }[];
    data.forEach((d) => {
      switch (d.type) {
        case FilterTypes.multi:
          const t = d.options.filter((e) => e.selected);
          const active = t.length < d.options.length;
          if (t.length) {
            const SelectedCompareRefiner = CompareRefiners[snakeToCamelCase(d.columnName)];
            if (!!SelectedCompareRefiner) {
              const value = t.map((e) => e.key).join(',');
              Object.keys(SelectedCompareRefiner)
                .filter((k) => value === k)
                .forEach((k) => {
                  filters.push(
                    ...SelectedCompareRefiner[k].map((e) => ({
                      key: `${d.apiRefinerType}_${d.columnName}${e.key}`,
                      type: d.type,
                      active,
                      value: e.value,
                    })),
                  );
                });
            } else {
              filters.push({
                key: `${d.apiRefinerType}_${d.columnName}`,
                type: d.type,
                active,
                value: t.map((e) => e.key).join(','),
              });
            }
            if (active)
              params.push({
                key: d.key,
                value: t.map((e) => e.key).join(','),
              });
            else
              params.push({
                key: d.key,
                value: 'ALL',
              });
          }
          break;
        case FilterTypes.single:
          const selectedValue = d.options.filter((e) => e.selected)[0];
          if (selectedValue) {
            filters.push({
              key: `${d.apiRefinerType}_${d.columnName}`,
              type: d.type,
              value: selectedValue.key,
            });
            params.push({
              key: d.key,
              value: selectedValue.key,
            });
          }
          break;
        case FilterTypes.query:
          if (d.value) {
            if (Array.isArray(d.columnName)) {
              filters.push({
                key: `${d.apiRefinerType}_fields`,
                type: d.type,
                value: d.columnName.join(','),
              });
              filters.push({
                key: `${d.apiRefinerType}_term`,
                type: d.type,
                value: d.value,
              });
            } else {
              filters.push({
                key: `${d.apiRefinerType}_${d.columnName}`,
                type: d.type,
                value: d.value,
              });
            }

            params.push({
              key: d.key,
              value: d.value,
            });
          }
          break;
        case FilterTypes.queryMultiple:
          if (d.value) {
            filters.push({
              key: `${d.apiRefinerType}_${d.columnName}`,
              type: d.type,
              value: d.value,
            });
            params.push({
              key: d.key,
              value: d.value,
            });
          }
          break;
        case FilterTypes.switch:
          if (typeof d.value === 'boolean') {
            filters.push({
              key: `${d.apiRefinerType}_${d.columnName}`,
              type: d.type,
              value: d.value ? 'true' : 'false',
            });
            params.push({
              key: d.key,
              value: d.value ? 'true' : 'false',
            });
          }
          break;
        case FilterTypes.compare:
          const tmp = getMetricFilterAPIParams(d);
          filters.push(...tmp.filters);
          params.push(...tmp.params);
          break;
      }
    });
    return { filters, params };
  }
  getFiltersForAPI(selectionQuery, filters) {
    return (
      this.getQueryAndSelectedFilters(this.getSelectedFilters(selectionQuery, filters)).filters.map((d) => [
        d.key,
        d.value,
      ]) || []
    );
  }
  getAccountSubFilters(): SubFilter[] {
    const filters = [];
    Object.keys(accountFilters).forEach((key) => {
      const tmp: SubFilter = { ...accountFilters[key] };
      switch (tmp.type) {
        case 'multi-select':
          tmp.options.forEach((d) => (d.selected = false));
          break;
        case 'query':
          break;
      }
      filters.push(tmp);
    });
    return filters;
  }
  getPGSubFilters(excludeFilters = []): SubFilter[] {
    const filters = [];
    Object.keys(PGFilters).forEach((key) => {
      if (excludeFilters.includes(key)) return;

      const tmp: SubFilter = { ...PGFilters[key] };
      switch (tmp.type) {
        case 'multi-select':
          tmp.options.forEach((d) => (d.selected = false));
          break;
      }
      filters.push(tmp);
    });
    return filters;
  }

  getCampaignCreationPGFilters(excludeFilters = []): SubFilter[] {
    const filters = [];
    Object.keys(CampaignCreationPGFilters).forEach((key) => {
      if (excludeFilters.includes(key)) return;

      const tmp: SubFilter = { ...CampaignCreationPGFilters[key] };
      switch (tmp.type) {
        case 'multi-select':
          tmp.options.forEach((d) => (d.selected = false));
          break;
      }
      filters.push(tmp);
    });
    return filters;
  }

  getCampaignSubFilters(excludeFilters = []): SubFilter[] {
    const filters = [];
    const tempFilter = this._getCampaignFilterForEbay();
    Object.keys(tempFilter).forEach((key) => {
      if (excludeFilters.includes(key)) return;

      const tmp: SubFilter = { ...tempFilter[key] };
      switch (tmp.type) {
        case 'multi-select':
          tmp.options.forEach((d) => (d.selected = false));
          break;
        case 'query':
          break;
      }
      filters.push(tmp);
    });
    return filters;
  }

  private _getCampaignFilterForEbay(temp: any = { ...campaignFilters }) {
    if (this.summary.accountPlatforms.includes('EBAY') && this.summary.campaignEbyMarketplaceIds?.length) {
      const options = this.summary.campaignEbyMarketplaceIds.map((key) => ({
        key,
        active: true,
        text: this.translations['sub-filters']['marketplace-options'][key],
      }));

      temp['ebayMarketplace'] = {
        options,
        allOptions: options,
        type: 'multi-select',
        key: 'ebayMarketplace',
        apiRefinerType: 'filter',
        columnName: 'campaign_eby_marketplace_id',
        displayName: this.translations['sub-filters']['ebay-marketplace'],
      };
    }

    return temp;
  }
  getcampaignsPGSubFilters(optiType: CampaignOptimizationValue, currency): SubFilter[] {
    // for PG wizard
    const filters = [];
    Object.keys(campaignsPGFilters).forEach((key) => {
      const tmp: SubFilter = { ...campaignsPGFilters[key] };
      switch (tmp.type) {
        case 'multi-select':
          tmp.options.forEach((d) => (d.selected = false));
          filters.push(tmp);
          break;
        case 'query':
        case 'switch':
          filters.push(tmp);
          break;
        case 'compare':
          if (tmp.optiTypes.includes(optiType)) {
            if (tmp.subType === 'currency') tmp.currency = currency;

            filters.push(tmp);
          }
          break;
      }
    });
    return filters;
  }
  getGoalsPGSubFilters(): SubFilter[] {
    // for manage campaigns flow
    const filters = [];
    Object.keys(goalsPGFilters).forEach((key) => {
      const tmp: SubFilter = { ...goalsPGFilters[key] };
      switch (tmp.type) {
        case 'multi-select':
          tmp.options.forEach((d) => (d.selected = false));
          filters.push(tmp);
          break;
        case 'query':
        case 'switch':
          filters.push(tmp);
          break;
      }
    });
    return filters;
  }
  getAdGroupsSubFilters(): SubFilter[] {
    const filters = [];
    Object.keys(adGroupFilters).forEach((key) => {
      const tmp: SubFilter = { ...adGroupFilters[key] };
      switch (tmp.type) {
        case 'multi-select':
          tmp.options.forEach((d) => (d.selected = false));
          break;
        case 'query':
          break;
      }
      filters.push(tmp);
    });
    return filters;
  }

  getDestinationAdGroupSubFilters(excludeFilters = [], skipEbay?: boolean): SubFilter[] {
    const filters = [];
    const tempFilter = skipEbay
      ? { ...destinationAdGroupFilters }
      : this._getCampaignFilterForEbay({ ...destinationAdGroupFilters });

    Object.keys(tempFilter).forEach((key) => {
      if (excludeFilters.includes(key)) return;

      const tmp: SubFilter = { ...tempFilter[key] };
      switch (tmp.type) {
        case 'multi-select':
          tmp.options.forEach((d) => (d.selected = false));
          break;
        case 'query':
          break;
      }
      filters.push(tmp);
    });
    return filters;
  }

  getCriteriaSubFilters(): SubFilter[] {
    const filters = [];
    Object.keys(criteriaFilters).forEach((key) => {
      const tmp: SubFilter = { ...criteriaFilters[key] };
      switch (tmp.type) {
        case 'multi-select':
          tmp.options.forEach((d) => (d.selected = false));
          break;
      }
      filters.push(tmp);
    });
    return filters;
  }
  getSuggestionsSubFilters(): SubFilter[] {
    const filters = [];
    Object.keys(suggestionsFilters).forEach((key) => {
      const tmp: SubFilter = { ...suggestionsFilters[key] };
      switch (tmp.type) {
        case 'multi-select':
          tmp.options.forEach((d) => (d.selected = false));
          break;
      }
      filters.push(tmp);
    });
    return filters;
  }

  getRulesSubFilters(currentTab: RulesTab): SubFilter[] {
    const filters = [];
    Object.keys(rulesFilters)
      .filter((key) => currentTab === RulesTab.Adgroup || key !== 'ruleProductAdSyncMode')
      .forEach((key) => {
        const tmp: SubFilter = { ...rulesFilters[key] };
        switch (tmp.type) {
          case 'multi-select':
            tmp.options.forEach((d) => (d.selected = false));
            break;
        }
        filters.push(tmp);
      });
    return filters;
  }
  setRefinerOptions(data: any = this.summary) {
    const {
      campaignOptimizationTypes = null,
      campaignTypes = null,
      accountPlatforms = null,
      hasAmzRasCampaigns,
    } = data;
    if (accountPlatforms) {
      accountFilters.platform.options = accountFilters.platform.allOptions.filter(
        (d) => !!accountPlatforms.find((e) => e === d.key),
      );
      campaignsPGFilters.platform.options = accountFilters.platform.allOptions.filter(
        (d) => !!accountPlatforms.find((e) => e === d.key),
      );
      goalsPGFilters.platform.options = accountFilters.platform.allOptions.filter(
        (d) => !!accountPlatforms.find((e) => e === d.key),
      );
    }
    if (campaignOptimizationTypes) {
      PGFilters.optiType.options = PGFilters.optiType.allOptions.filter(
        (d) => !!campaignOptimizationTypes.find((e) => e === d.key),
      );
      campaignFilters.optiType.options = campaignFilters.optiType.allOptions.filter(
        (d) => !!campaignOptimizationTypes.find((e) => e === d.key),
      );

      PGFilters.goalType.options = PGFilters.goalType.allOptions.filter((d) =>
        campaignOptimizationTypes
          .reduce((acc, curr) => [...acc, ...PerformanceGroupGoalType[curr]], [])
          .includes(d.key),
      );
      PGFilters.campaignsCount.options = [...PGFilters.campaignsCount.allOptions];
      goalsPGFilters.campaignsCount.options = [...PGFilters.campaignsCount.allOptions];

      CampaignCreationPGFilters.goalType.options = CampaignCreationPGFilters.goalType.allOptions.filter((d) =>
        campaignOptimizationTypes
          .reduce((acc, curr) => [...acc, ...PerformanceGroupGoalType[curr]], [])
          .includes(d.key),
      );
    }
    if (campaignTypes) {
      const options = campaignFilters.type.allOptions.filter((d) => campaignTypes.some((e) => e === d.key));

      campaignFilters.type.options = [...options];
      campaignsPGFilters.type.options = [...options];
      destinationAdGroupFilters.campaignType.options = [...options];
    }

    campaignFilters.ras.disabled = !hasAmzRasCampaigns;
    adGroupFilters.retailers.disabled = !hasAmzRasCampaigns;
  }
  constructor(
    private translate: TranslateService,
    private store: Store,
  ) {
    this.summary = this.store.selectSnapshot(CustomerState.summary);
    const customersHasNewGoalsPage = this.store.selectSnapshot(CustomerState.customersHasNewGoalsPage);

    const data = this.translate.instant([
      'global-filter',
      'common.metrics',
      'common.goal-types',
      'common.campaigns-count',
      'ca',
    ]);

    this.translations = data['global-filter'];
    const subFilters = this.translations['sub-filters'];
    accountFilters.campaignOptimize.displayName = subFilters.optimize;
    accountFilters.platform.displayName = subFilters.platform;
    accountFilters.tags.displayName = subFilters.tags;

    PGFilters.enabled.displayName = subFilters.optimize;
    PGFilters.optiType.displayName = subFilters.optiType;
    PGFilters.optiType.allOptions.forEach((d) => (d.text = subFilters[d.key]));
    PGFilters.goalType.displayName = subFilters.goalType;
    PGFilters.goalType.allOptions.forEach((d) => {
      d.text =
        customersHasNewGoalsPage && d.key === 'COST-PER-DAY'
          ? data['common.goal-types']['maximize_sales']
          : data['common.goal-types'][d.key];
    });
    PGFilters.campaignsCount.displayName = subFilters.campaignsCount;
    PGFilters.campaignsCount.allOptions.forEach((d) => (d.text = data['common.campaigns-count'][d.key]));

    CampaignCreationPGFilters.pgName.displayName = subFilters['pg-name'];
    CampaignCreationPGFilters.enabled.displayName = subFilters.optimize;
    CampaignCreationPGFilters.goalType.displayName = subFilters.goalType;
    CampaignCreationPGFilters.goalType.allOptions.forEach((d) => (d.text = data['common.goal-types'][d.key]));

    campaignFilters.name.displayName = subFilters['campaign-name'];
    campaignFilters.status.displayName = subFilters['campaign-status'];
    campaignFilters.campaignOptimize.displayName = subFilters.optimize;
    campaignFilters.type.displayName = subFilters['campaign-type'];
    campaignFilters.optiType.displayName = subFilters.optiType;
    campaignFilters.status.options.forEach((d) => (d.text = subFilters[d.key]));
    campaignFilters.optiType.allOptions.forEach((d) => (d.text = subFilters[d.key]));
    campaignFilters.portfolio.displayName = subFilters.portfolio;
    campaignFilters.label.displayName = subFilters.label;

    campaignFilters.ras.displayName = subFilters['campaign-ras'];
    campaignFilters.ras.options.forEach((d) => (d.text = subFilters['campaign-ras-' + d.key]));

    adGroupFilters.retailers.displayName = subFilters.retailers;
    adGroupFilters.status.displayName = subFilters['adgrp-status'];
    adGroupFilters.name.displayName = subFilters['adgrp-name'];
    adGroupFilters.status.options.forEach((d) => (d.text = subFilters[d.key]));

    criteriaFilters.status.displayName = subFilters['criteria-status'];
    criteriaFilters.type.displayName = subFilters['criteria-type'];
    criteriaFilters.matchType.displayName = subFilters['criteria-matchType'];
    criteriaFilters.name.displayName = subFilters['criteria-name'];

    campaignsPGFilters.status.displayName = subFilters['campaign-status'];
    campaignsPGFilters.status.options.forEach((d) => (d.text = subFilters[d.key]));
    campaignsPGFilters.type.displayName = subFilters['campaign-type'];
    campaignsPGFilters.tags.displayName = subFilters.tags;
    campaignsPGFilters.accounts.displayName = subFilters.accounts;
    campaignsPGFilters.pg.displayName = subFilters.pg;
    campaignsPGFilters.portfolio.displayName = subFilters.portfolio;
    campaignsPGFilters.label.displayName = subFilters.label;
    campaignsPGFilters.platform.displayName = subFilters.platform;
    campaignsPGFilters[COST].displayName = data['common.metrics'][COST].title;
    campaignsPGFilters[IMPRESSIONS].displayName = data['common.metrics'][IMPRESSIONS].title;
    campaignsPGFilters[CLICKS].displayName = data['common.metrics'][CLICKS].title;
    campaignsPGFilters[ROAS].displayName = data['common.metrics'][ROAS].title;
    campaignsPGFilters[COST_PER_CONVERSION].displayName = data['common.metrics'][COST_PER_CONVERSION].title;
    campaignsPGFilters[COST_REVENUE_RATIO].displayName = data['common.metrics'][COST_REVENUE_RATIO].title;
    campaignsPGFilters[RETURN_ON_INVESTMENT].displayName = data['common.metrics'][RETURN_ON_INVESTMENT].title;
    campaignsPGFilters[CONVERSIONS].displayName = data['common.metrics'][CONVERSIONS].title;
    campaignsPGFilters.campaignOptimize.displayName = subFilters.optimize;

    goalsPGFilters.accounts.displayName = subFilters.accounts;
    goalsPGFilters.platform.displayName = subFilters.platform;
    goalsPGFilters.enabled.displayName = subFilters.optimize;

    goalsPGFilters.campaignsCount.displayName = subFilters.campaignsCount;
    goalsPGFilters.campaignsCount.allOptions.forEach((d) => (d.text = data['common.campaigns-count'][d.key]));

    destinationAdGroupFilters.adgroupName.displayName = subFilters['adgrp-name'];
    destinationAdGroupFilters.adgroupStatus.displayName = subFilters['adgrp-status'];
    destinationAdGroupFilters.amzProductAdsMatchRatio.displayName = subFilters['amz-product-ads-match-ratio'];
    destinationAdGroupFilters.campaignName.displayName = subFilters['campaign-name'];
    destinationAdGroupFilters.campaignStatus.displayName = subFilters['campaign-status'];
    destinationAdGroupFilters.campaignStatus.displayName = subFilters['campaign-status'];
    destinationAdGroupFilters.campaignType.displayName = subFilters['campaign-type'];
    destinationAdGroupFilters.campaignOptimize.displayName = subFilters['campaign-optimize'];
    destinationAdGroupFilters.performanceGroupName.displayName = subFilters['performance-group-name'];
    destinationAdGroupFilters.performanceGroupEnabled.displayName = subFilters['pg-optimize'];

    rulesFilters.ruleAction.displayName = subFilters.action;
    rulesFilters.ruleInherited.displayName = subFilters['rule-inherited'];
    rulesFilters.ruleAction.allOptions.forEach((d) => (d.text = subFilters[d.key]));
    rulesFilters.ruleInherited.allOptions.forEach(
      (d) => (d.text = subFilters[d.key === 'false' ? 'non-inherited' : 'inherited']),
    );
    rulesFilters.ruleTargetingType.displayName = subFilters['rule-targeting-type'];
    rulesFilters.ruleMatchType.displayName = subFilters['rule-match-type'];
    rulesFilters.ruleNegative.displayName = subFilters['rule-negative-keyword'];
    rulesFilters.ruleProductAdSyncMode.displayName = subFilters['rule-product-ad-sync-mode'];

    const setOptions = (source: SubFilter, key: string): void => {
      source.allOptions.forEach((d) => (d.text = subFilters[`${key}-${d.key}`]));
      source.options = [...source.allOptions];
    };

    setOptions(rulesFilters.ruleTargetingType, 'targeting');
    setOptions(rulesFilters.ruleMatchType, 'match-type');
    setOptions(rulesFilters.ruleNegative, 'negative');
    setOptions(rulesFilters.ruleProductAdSyncMode, 'ad-sync-mode');

    rulesFilters.ruleInherited.options = [...rulesFilters.ruleInherited.allOptions];
    rulesFilters.ruleAction.options = [...rulesFilters.ruleAction.allOptions];

    [
      ...accountFilters.campaignOptimize.options,
      ...PGFilters.enabled.options,
      ...CampaignCreationPGFilters.enabled.options,
      ...goalsPGFilters.enabled.options,
      ...campaignsPGFilters.campaignOptimize.options,
      ...campaignFilters.campaignOptimize.options,
      ...destinationAdGroupFilters.campaignOptimize.options,
      ...destinationAdGroupFilters.performanceGroupEnabled.options,
    ].forEach((option) => {
      option.text = option.key === 'true' ? subFilters.optimized : subFilters['not-optimized'];
    });

    suggestionsFilters.proposalType.displayName = subFilters['proposal-type'];
    suggestionsFilters.criterionType.displayName = subFilters['criterion-type'];
    suggestionsFilters.proposalReasons.displayName = subFilters['proposal-reasons'];
    suggestionsFilters.criterionName.displayName = subFilters['criterion-name'];

    suggestionsFilters.proposalType.allOptions.forEach((d) => (d.text = data.ca.rules[d.key]));
    suggestionsFilters.criterionType.allOptions.forEach((d) => (d.text = data.ca['criterion-types'][d.key]));
    suggestionsFilters.proposalReasons.allOptions.forEach((d) => (d.text = data.ca.suggestions.reasons[d.key]));

    suggestionsFilters.proposalType.options = [...suggestionsFilters.proposalType.allOptions];
    suggestionsFilters.criterionType.options = [...suggestionsFilters.criterionType.allOptions].filter((d) =>
      d.platform.find(({ name }) => this.summary.accountPlatforms?.includes(<AdPlatform>name)),
    );
    suggestionsFilters.proposalReasons.options = [...suggestionsFilters.proposalReasons.allOptions];
  }
}
